export type SelmoFontSize = "small" | "default" | "large";
export type SelmoDensity = "compact" | "basic" | "comfortable";
export type SelmoAccent = "classic" | "sunflower" | "ochre";
export type SelmoCardSpacing = "tight" | "basic" | "relaxed";
export type SelmoCardRadius = "subtle" | "basic" | "rounded";

export type AppearanceSizeKey =
  | "pageTitleSize"
  | "sectionTitleSize"
  | "subsectionTitleSize"
  | "bodyFontSize"
  | "metaFontSize"
  | "keyValueSize"
  | "globalSidebarWidth"
  | "projectRailWidth"
  | "contentMaxWidth";

export interface AppearancePreferences {
  fontSize: SelmoFontSize;
  density: SelmoDensity;
  accent: SelmoAccent;
  cardSpacing: SelmoCardSpacing;
  cardRadius: SelmoCardRadius;
  pageTitleSize: number;
  sectionTitleSize: number;
  subsectionTitleSize: number;
  bodyFontSize: number;
  metaFontSize: number;
  keyValueSize: number;
  globalSidebarWidth: number;
  projectRailWidth: number;
  contentMaxWidth: number;
}

export const APPEARANCE_STORAGE_KEY = "project-selmo:appearance:v1";

export const DEFAULT_APPEARANCE_PREFERENCES: AppearancePreferences = {
  fontSize: "default",
  density: "basic",
  accent: "classic",
  cardSpacing: "basic",
  cardRadius: "basic",
  pageTitleSize: 28,
  sectionTitleSize: 20,
  subsectionTitleSize: 16,
  bodyFontSize: 14,
  metaFontSize: 12,
  keyValueSize: 21,
  globalSidebarWidth: 184,
  projectRailWidth: 300,
  contentMaxWidth: 1240,
};

export const CONSTRUCTION_APPEARANCE_PRESET: Pick<AppearancePreferences, AppearanceSizeKey> = {
  pageTitleSize: 28,
  sectionTitleSize: 20,
  subsectionTitleSize: 16,
  bodyFontSize: 14,
  metaFontSize: 12,
  keyValueSize: 21,
  globalSidebarWidth: 184,
  projectRailWidth: 260,
  contentMaxWidth: 1240,
};

export const RECOMMENDED_APPEARANCE_PRESET: Pick<AppearancePreferences, AppearanceSizeKey> = {
  ...CONSTRUCTION_APPEARANCE_PRESET,
  projectRailWidth: 300,
};

const OPTIONS = {
  fontSize: ["small", "default", "large"],
  density: ["compact", "basic", "comfortable"],
  accent: ["classic", "sunflower", "ochre"],
  cardSpacing: ["tight", "basic", "relaxed"],
  cardRadius: ["subtle", "basic", "rounded"],
} as const;

const LEGACY_FONT_PRESETS: Record<SelmoFontSize, Pick<AppearancePreferences,
  "pageTitleSize" | "sectionTitleSize" | "subsectionTitleSize" | "bodyFontSize" | "metaFontSize" | "keyValueSize"
>> = {
  small: { pageTitleSize: 25, sectionTitleSize: 18, subsectionTitleSize: 15, bodyFontSize: 13, metaFontSize: 12, keyValueSize: 19 },
  default: { pageTitleSize: 28, sectionTitleSize: 20, subsectionTitleSize: 16, bodyFontSize: 14, metaFontSize: 12, keyValueSize: 21 },
  large: { pageTitleSize: 32, sectionTitleSize: 22, subsectionTitleSize: 18, bodyFontSize: 15, metaFontSize: 13, keyValueSize: 23 },
};

const SIZE_LIMITS: Record<AppearanceSizeKey, readonly [number, number]> = {
  pageTitleSize: [24, 36],
  sectionTitleSize: [16, 24],
  subsectionTitleSize: [14, 20],
  bodyFontSize: [13, 17],
  metaFontSize: [11, 14],
  keyValueSize: [18, 28],
  globalSidebarWidth: [168, 220],
  projectRailWidth: [260, 360],
  contentMaxWidth: [1080, 1600],
};

function browserStorage(): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function allowed<K extends keyof typeof OPTIONS>(key: K, value: unknown): value is AppearancePreferences[K] {
  return typeof value === "string" && (OPTIONS[key] as readonly string[]).includes(value);
}

function normalizedSize(key: AppearanceSizeKey, value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  const [minimum, maximum] = SIZE_LIMITS[key];
  return Math.min(maximum, Math.max(minimum, Math.round(value)));
}

export function normalizeAppearancePreferences(value: unknown): AppearancePreferences {
  if (!value || typeof value !== "object") return { ...DEFAULT_APPEARANCE_PREFERENCES };
  const input = value as Partial<Record<keyof AppearancePreferences, unknown>>;
  const fontSize = allowed("fontSize", input.fontSize) ? input.fontSize : DEFAULT_APPEARANCE_PREFERENCES.fontSize;
  const legacyFontPreset = LEGACY_FONT_PRESETS[fontSize];
  return {
    fontSize,
    density: allowed("density", input.density) ? input.density : DEFAULT_APPEARANCE_PREFERENCES.density,
    accent: allowed("accent", input.accent) ? input.accent : DEFAULT_APPEARANCE_PREFERENCES.accent,
    cardSpacing: allowed("cardSpacing", input.cardSpacing) ? input.cardSpacing : DEFAULT_APPEARANCE_PREFERENCES.cardSpacing,
    cardRadius: allowed("cardRadius", input.cardRadius) ? input.cardRadius : DEFAULT_APPEARANCE_PREFERENCES.cardRadius,
    pageTitleSize: normalizedSize("pageTitleSize", input.pageTitleSize, legacyFontPreset.pageTitleSize),
    sectionTitleSize: normalizedSize("sectionTitleSize", input.sectionTitleSize, legacyFontPreset.sectionTitleSize),
    subsectionTitleSize: normalizedSize("subsectionTitleSize", input.subsectionTitleSize, legacyFontPreset.subsectionTitleSize),
    bodyFontSize: normalizedSize("bodyFontSize", input.bodyFontSize, legacyFontPreset.bodyFontSize),
    metaFontSize: normalizedSize("metaFontSize", input.metaFontSize, legacyFontPreset.metaFontSize),
    keyValueSize: normalizedSize("keyValueSize", input.keyValueSize, legacyFontPreset.keyValueSize),
    globalSidebarWidth: normalizedSize("globalSidebarWidth", input.globalSidebarWidth, DEFAULT_APPEARANCE_PREFERENCES.globalSidebarWidth),
    projectRailWidth: normalizedSize("projectRailWidth", input.projectRailWidth, DEFAULT_APPEARANCE_PREFERENCES.projectRailWidth),
    contentMaxWidth: normalizedSize("contentMaxWidth", input.contentMaxWidth, DEFAULT_APPEARANCE_PREFERENCES.contentMaxWidth),
  };
}

export function readAppearancePreferences(storage?: Pick<Storage, "getItem">): AppearancePreferences {
  const target = storage ?? browserStorage();
  if (!target) return { ...DEFAULT_APPEARANCE_PREFERENCES };
  try {
    const stored = target.getItem(APPEARANCE_STORAGE_KEY);
    return stored ? normalizeAppearancePreferences(JSON.parse(stored)) : { ...DEFAULT_APPEARANCE_PREFERENCES };
  } catch {
    return { ...DEFAULT_APPEARANCE_PREFERENCES };
  }
}

export function applyAppearancePreferences(
  preferences: AppearancePreferences,
  root?: Pick<HTMLElement, "dataset"> & { style?: Pick<CSSStyleDeclaration, "setProperty"> },
): void {
  const target = root ?? (typeof document === "undefined" ? undefined : document.documentElement);
  if (!target) return;
  target.dataset.selmoFontSize = preferences.fontSize;
  target.dataset.selmoDensity = preferences.density;
  target.dataset.selmoAccent = preferences.accent;
  target.dataset.selmoCardSpacing = preferences.cardSpacing;
  target.dataset.selmoCardRadius = preferences.cardRadius;
  target.style?.setProperty("--selmo-ui-page-title-size", `${preferences.pageTitleSize}px`);
  target.style?.setProperty("--selmo-ui-section-title-size", `${preferences.sectionTitleSize}px`);
  target.style?.setProperty("--selmo-ui-subsection-title-size", `${preferences.subsectionTitleSize}px`);
  target.style?.setProperty("--selmo-ui-font-size", `${preferences.bodyFontSize}px`);
  target.style?.setProperty("--selmo-ui-small-font-size", `${preferences.metaFontSize}px`);
  target.style?.setProperty("--selmo-ui-value-size", `${preferences.keyValueSize}px`);
  target.style?.setProperty("--selmo-ui-card-value-size", `${Math.max(preferences.bodyFontSize, preferences.keyValueSize - 7)}px`);
  target.style?.setProperty("--selmo-ui-global-sidebar-width", `${preferences.globalSidebarWidth}px`);
  target.style?.setProperty("--selmo-ui-project-rail-width", `${preferences.projectRailWidth}px`);
  target.style?.setProperty("--selmo-ui-content-max-width", `${preferences.contentMaxWidth}px`);
}

export function saveAppearancePreferences(
  preferences: AppearancePreferences,
  storage?: Pick<Storage, "setItem">,
): void {
  const normalized = normalizeAppearancePreferences(preferences);
  const target = storage ?? browserStorage();
  try {
    target?.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    // Private browsing or a managed browser may deny local storage. The live
    // preference still applies for the current page without touching a server.
  }
  applyAppearancePreferences(normalized);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("selmo:appearance-change", { detail: normalized }));
  }
}

export function resetAppearancePreferences(storage?: Pick<Storage, "removeItem">): AppearancePreferences {
  const target = storage ?? browserStorage();
  try {
    target?.removeItem(APPEARANCE_STORAGE_KEY);
  } catch {
    // Keep the in-memory reset even when local storage is unavailable.
  }
  const defaults = { ...DEFAULT_APPEARANCE_PREFERENCES };
  applyAppearancePreferences(defaults);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("selmo:appearance-change", { detail: defaults }));
  }
  return defaults;
}

export function applyStoredAppearancePreferences(): AppearancePreferences {
  const preferences = readAppearancePreferences();
  applyAppearancePreferences(preferences);
  return preferences;
}

// Keep the original v1 bytes and public/legacy rendering independent of the
// authenticated employee UI. A separate commit marker makes partial writes
// recoverable without ever replacing the source settings.
export const STAFF_APPEARANCE_STORAGE_KEY = "project-selmo:appearance:five-role:v1";
export const STAFF_APPEARANCE_BACKUP_KEY = "project-selmo:appearance:five-role:original:v1";
export const STAFF_APPEARANCE_MIGRATION_KEY = "project-selmo:appearance:five-role:complete:v1";
const STAFF_MIGRATION_COMPLETE = "five-role-v1";
export const FONT_ROLE_KEYS = ["pageTitleSize", "sectionTitleSize", "subsectionTitleSize", "bodyFontSize", "metaFontSize"] as const;
export type FontRoleKey = typeof FONT_ROLE_KEYS[number];
export const DEFAULT_FONT_ROLES: Pick<AppearancePreferences, FontRoleKey> = {
  pageTitleSize: 24, sectionTitleSize: 20, subsectionTitleSize: 16, bodyFontSize: 14, metaFontSize: 12,
};
export const DEFAULT_STAFF_APPEARANCE_PREFERENCES: AppearancePreferences = {
  ...DEFAULT_APPEARANCE_PREFERENCES, ...DEFAULT_FONT_ROLES,
};
type AppearanceStorage = Pick<Storage, "getItem" | "setItem">;
type AppearanceRoot = Pick<HTMLElement, "dataset"> & { style?: Pick<CSSStyleDeclaration, "setProperty"> };
export interface StaffAppearanceResult {
  preferences: AppearancePreferences;
  persisted: boolean;
}
const staffSessions = new WeakMap<object, AppearancePreferences>();
let volatileStaffPreferences: AppearancePreferences | undefined;

export function fontRoleBounds(preferences: AppearancePreferences, key: FontRoleKey): readonly [number, number] {
  const index = FONT_ROLE_KEYS.indexOf(key);
  const [legacyMinimum, maximum] = SIZE_LIMITS[key];
  const minimum = key === "bodyFontSize" ? 14 : key === "metaFontSize" ? 12 : legacyMinimum;
  const above = FONT_ROLE_KEYS[index - 1];
  const below = FONT_ROLE_KEYS[index + 1];
  return [Math.max(minimum, below ? preferences[below] + 2 : minimum), Math.min(maximum, above ? preferences[above] - 2 : maximum)];
}

export function isValidFontRoles(preferences: AppearancePreferences): boolean {
  return FONT_ROLE_KEYS.every((key) => {
    const [minimum, maximum] = fontRoleBounds(preferences, key);
    return Number.isInteger(preferences[key]) && preferences[key] >= minimum && preferences[key] <= maximum;
  });
}

function staffDefaultsFromLegacy(raw: string | null): AppearancePreferences {
  let legacy: unknown;
  try { legacy = raw ? JSON.parse(raw) : undefined; } catch { /* Preserve malformed source bytes in the backup. */ }
  return { ...normalizeAppearancePreferences(legacy), ...DEFAULT_FONT_ROLES, fontSize: "default" };
}

function readCommittedStaffPreferences(storage: Pick<Storage, "getItem">): AppearancePreferences | undefined {
  if (storage.getItem(STAFF_APPEARANCE_MIGRATION_KEY) !== STAFF_MIGRATION_COMPLETE) return undefined;
  const backup = storage.getItem(STAFF_APPEARANCE_BACKUP_KEY);
  if (backup === null) return undefined;
  let original: { sourceKey?: unknown; raw?: unknown } | null;
  try { original = JSON.parse(backup); } catch { return undefined; }
  if (!original) return undefined;
  if (original.sourceKey !== APPEARANCE_STORAGE_KEY || !(original.raw === null || typeof original.raw === "string")) return undefined;
  const raw = storage.getItem(STAFF_APPEARANCE_STORAGE_KEY);
  if (!raw) return undefined;
  let parsed: Partial<AppearancePreferences> | null;
  try { parsed = JSON.parse(raw); } catch { return undefined; }
  if (!parsed || typeof parsed !== "object") return undefined;
  const preferences = normalizeAppearancePreferences(parsed);
  // Reject missing/out-of-range/colliding roles instead of silently moving
  // another role to make an invalid edited value fit.
  if (!FONT_ROLE_KEYS.every((key) => parsed[key] === preferences[key]) || !isValidFontRoles(preferences)) return undefined;
  return preferences;
}

export function readStaffAppearancePreferences(storage?: Pick<Storage, "getItem">): AppearancePreferences {
  const target = storage ?? browserStorage();
  const session = target ? staffSessions.get(target) : volatileStaffPreferences;
  if (session) return { ...session };
  try {
    const committed = target ? readCommittedStaffPreferences(target) : undefined;
    if (committed) return committed;
  } catch { /* A blocked migration key must not hide readable legacy widths. */ }
  try {
    if (target) return staffDefaultsFromLegacy(target.getItem(APPEARANCE_STORAGE_KEY));
  } catch { /* A blocked browser storage still has usable in-memory defaults. */ }
  return { ...DEFAULT_STAFF_APPEARANCE_PREFERENCES };
}

function migrateStaffAppearance(storage: AppearanceStorage): StaffAppearanceResult {
  const existing = readCommittedStaffPreferences(storage);
  if (existing) return { preferences: existing, persisted: true };
  const raw = storage.getItem(APPEARANCE_STORAGE_KEY);
  const preferences = staffDefaultsFromLegacy(raw);
  const backup = JSON.stringify({ sourceKey: APPEARANCE_STORAGE_KEY, raw });
  const originalBackup = storage.getItem(STAFF_APPEARANCE_BACKUP_KEY);
  if (originalBackup === null) storage.setItem(STAFF_APPEARANCE_BACKUP_KEY, backup);
  // A prior failed attempt may already have captured the immutable original.
  // Never overwrite it or mark an unrelated source as migrated.
  if (storage.getItem(STAFF_APPEARANCE_BACKUP_KEY) !== backup) return { preferences, persisted: false };
  const next = JSON.stringify(preferences);
  storage.setItem(STAFF_APPEARANCE_STORAGE_KEY, next);
  if (storage.getItem(STAFF_APPEARANCE_STORAGE_KEY) !== next) return { preferences, persisted: false };
  storage.setItem(STAFF_APPEARANCE_MIGRATION_KEY, STAFF_MIGRATION_COMPLETE);
  return { preferences, persisted: storage.getItem(STAFF_APPEARANCE_MIGRATION_KEY) === STAFF_MIGRATION_COMPLETE };
}

const STAFF_ACCENT_COLORS: Record<SelmoAccent, readonly [string, string, string]> = {
  classic: ["#f3cc3e", "#dfb51d", "#fff8dc"],
  sunflower: ["#ffd43b", "#ddb400", "#fff8cf"],
  ochre: ["#e8b83f", "#c99013", "#fff4d2"],
};

export function applyStaffAppearancePreferences(preferences: AppearancePreferences, root: AppearanceRoot): void {
  applyAppearancePreferences(preferences, root);
  const [accent, strong, soft] = STAFF_ACCENT_COLORS[preferences.accent];
  root.style?.setProperty("--staff-primary", accent);
  root.style?.setProperty("--staff-primary-line", strong);
  root.style?.setProperty("--staff-primary-soft", soft);
  root.style?.setProperty("--staff-selected", soft);
  root.style?.setProperty("--staff-mobile-page-padding", `${preferences.density === "compact" ? 8 : preferences.density === "comfortable" ? 12 : 10}px`);

  root.style?.setProperty("--selmo-ui-value-size", `${preferences.pageTitleSize}px`);
  root.style?.setProperty("--selmo-ui-card-value-size", `${preferences.bodyFontSize}px`);
  root.style?.setProperty("--selmo-ui-card-radius", `${preferences.cardRadius === "rounded" ? 26 : 12}px`);
  root.style?.setProperty("--selmo-ui-gap", `${preferences.cardSpacing === "tight" ? 8 : preferences.cardSpacing === "relaxed" ? 22 : 16}px`);
  root.style?.setProperty("--selmo-ui-page-padding", `${preferences.density === "compact" ? 14 : preferences.density === "comfortable" ? 28 : 24}px`);
}

function rememberStaffPreferences(preferences: AppearancePreferences, storage?: Pick<Storage, "getItem">): void {
  if (storage) staffSessions.set(storage, { ...preferences });
  else volatileStaffPreferences = { ...preferences };
}

export function initializeStaffAppearancePreferences(root: AppearanceRoot, storage?: AppearanceStorage): StaffAppearanceResult {
  const target = storage ?? browserStorage();
  let result: StaffAppearanceResult = { preferences: readStaffAppearancePreferences(target), persisted: false };
  try {
    if (target) {
      const migration = migrateStaffAppearance(target);
      // Do not reset edits made in this tab when storage was unavailable.
      const preferences = staffSessions.get(target) ?? migration.preferences;
      result = { preferences, persisted: migration.persisted && target.getItem(STAFF_APPEARANCE_STORAGE_KEY) === JSON.stringify(preferences) };
    }
  } catch { /* Backup/source are never overwritten on a failed write. */ }
  rememberStaffPreferences(result.preferences, target);
  applyStaffAppearancePreferences(result.preferences, root);
  return result;
}

export function saveStaffAppearancePreferences(preferences: AppearancePreferences, storage?: AppearanceStorage): StaffAppearanceResult {
  if (!isValidFontRoles(preferences)) throw new RangeError("글자 역할 사이에는 최소 2px 차이가 필요합니다.");
  const target = storage ?? browserStorage();
  const normalized = normalizeAppearancePreferences(preferences);
  let persisted = false;
  try {
    if (target && migrateStaffAppearance(target).persisted) {
      const next = JSON.stringify(normalized);
      target.setItem(STAFF_APPEARANCE_STORAGE_KEY, next);
      persisted = target.getItem(STAFF_APPEARANCE_STORAGE_KEY) === next;
    }
  } catch { /* Keep this tab's edits usable without claiming they were saved. */ }
  rememberStaffPreferences(normalized, target);
  if (typeof document !== "undefined") {
    document.querySelectorAll<HTMLElement>("[data-staff-design]").forEach((root) => applyStaffAppearancePreferences(normalized, root));
  }
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("selmo:appearance-change", { detail: normalized }));
  return { preferences: normalized, persisted };
}

export function resetStaffFontRoles(storage?: AppearanceStorage): StaffAppearanceResult {
  return saveStaffAppearancePreferences({ ...readStaffAppearancePreferences(storage), ...DEFAULT_FONT_ROLES, fontSize: "default" }, storage);
}
